import React, { useState } from 'react';
import { Stack, IconButton, Text, TooltipHost } from '@fluentui/react';
import {
    useDisplayCreateWidgetModal,
    useCreateOrUpdateWidget,
    useEditedWidget,
    useIsUserAloneInOwnPersonalRoom
} from 'features/Rooms/PersonalMeetingCorner/states/hooks';
import {
    widgetCardStyles,
    widgetImageStyles,
    widgetImageOriginalStyles,
    widgetActionsStyles,
    headerStyles,
    footerStyles,
    editButtonStyles,
    deleteButtonStyles,
    infoIconContainerStyles,
    infoIconButtonStyles
} from '../SelectedWidgets.styles';
import EnlargeWidgets from './EnlargeWidgets/EnlargeWidgets';
import QuoteWidget from './QuoteWidget';
import URLWidget from './URLWidget';
import { IWidgetCardProps } from '../types';
import IvicosStrings from 'kits/language/stringKit';

const WidgetCard: React.FC<IWidgetCardProps> = ({ widget, onDelete }) => {
    const [showImageModal, setShowImageModal] = useState(false);
    const [, setDisplayCreateWidgetModal] = useDisplayCreateWidgetModal();
    const [, setCreateOrUpdateWidget] = useCreateOrUpdateWidget();
    const [, setEditedWidget] = useEditedWidget();
    const isUserAloneInOwnPersonalRoom = useIsUserAloneInOwnPersonalRoom();

    const { content, header, footer, id, type } = widget;
    const imageUrl = content?.imageUrl;
    const altText = content?.alt || 'widget-image';
    const aspectRatio = content?.aspectRatio || 'consistent';
    const bookCoverUrl = content?.coverUrl;
    const author = content?.author;
    const authorUrl = content?.authorUrl;

    const getImageStyle = () => {
        if (aspectRatio === 'consistent') return widgetImageStyles;
        return widgetImageOriginalStyles;
    };

    const getCardStyle = () => {
        return widgetCardStyles;
    };

    const handleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setDisplayCreateWidgetModal(widget.type);
        setCreateOrUpdateWidget('update');
        setEditedWidget(widget);
    };

    const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onDelete?.(id);
    };

    const handleCardClick = () => {
        if (imageUrl || bookCoverUrl) {
            setShowImageModal(true);
        }
    };

    const handleInfoClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (author && authorUrl) {
            const url = `${authorUrl}?utm_source=ivCAMPUS&utm_medium=referral`;
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    const renderInfoButton = () => {
        if (!imageUrl || !imageUrl.includes('unsplash') || !author || !authorUrl) return null;
        const photoByAuthor = IvicosStrings.personalMeetingCornerStrings.imageUpload.photoByAuthor;

        function renderAttributionString(str: string) {
            const regex = /(\{authorName\}|\{unsplash\})/g;
            const parts = str.split(regex);

            return parts.map((part, idx) => {
                if (part === '{authorName}') {
                    return (
                        <a key="author" href={authorUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'blue' }}>
                            {author}
                        </a>
                    );
                }
                if (part === '{unsplash}') {
                    return (
                        <a key="unsplash" href="https://unsplash.com" target="_blank" rel="noopener noreferrer" style={{ color: 'blue' }}>
                            Unsplash
                        </a>
                    );
                }
                return <React.Fragment key={idx}>{part}</React.Fragment>;
            });
        }

        const attributionContent = renderAttributionString(photoByAuthor);
        const iconBottom = footer ? 40 : 8;

        return (
            <div style={infoIconContainerStyles(iconBottom)}>
                <TooltipHost content={attributionContent}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <IconButton iconProps={{ iconName: 'Info' }} onClick={handleInfoClick} styles={infoIconButtonStyles} />
                    </div>
                </TooltipHost>
            </div>
        );
    };

    const renderWidgetContent = () => {
        if (widget.type === 'quote') {
            return <QuoteWidget content={content} setShowImageModal={setShowImageModal} />;
        }

        if (widget.type === 'urls') {
            return <URLWidget content={content} setShowImageModal={setShowImageModal} />;
        }

        if (imageUrl) {
            return <img src={imageUrl} alt={altText} className={getImageStyle()} />;
        }

        if (widget.type === 'book') {
            if (bookCoverUrl) {
                return <img src={bookCoverUrl} alt={IvicosStrings.personalMeetingCornerStrings.book.bookCover} className={getImageStyle()} />;
            }

            return (
                <Stack
                    style={{
                        width: 250,
                        height: 240,
                        backgroundColor: '#f0f0f0',
                        borderRadius: 10,
                        padding: 10,
                        cursor: 'pointer'
                    }}
                    verticalAlign="center"
                    horizontalAlign="center"
                    tokens={{
                        childrenGap: 20
                    }}
                    onClick={() => {
                        setShowImageModal(true);
                    }}
                >
                    <Text variant="xLarge" style={{ fontWeight: 600, color: '#000', textAlign: 'center', marginBottom: 20 }}>
                        {IvicosStrings.personalMeetingCornerStrings.book.fallbackBookCover.bookRecommendation}
                    </Text>
                    <Text variant="large" style={{ fontWeight: 400, color: '#000', textAlign: 'center' }}>
                        {IvicosStrings.personalMeetingCornerStrings.book.fallbackBookCover.clickHere}
                    </Text>
                </Stack>
            );
        }

        return null;
    };

    return (
        <>
            <Stack className={getCardStyle()} onClick={handleCardClick}>
                {renderWidgetContent()}
                {renderInfoButton()}
                {isUserAloneInOwnPersonalRoom && (
                    <Stack horizontal className={`widget-actions ${widgetActionsStyles}`}>
                        <TooltipHost content={IvicosStrings.personalMeetingCornerStrings.tooltips.editWidget}>
                            <IconButton iconProps={{ iconName: 'Edit' }} onClick={handleEdit} styles={editButtonStyles} />
                        </TooltipHost>
                        <TooltipHost content={IvicosStrings.personalMeetingCornerStrings.tooltips.deleteWidget}>
                            <IconButton iconProps={{ iconName: 'Delete' }} onClick={handleDelete} styles={deleteButtonStyles} />
                        </TooltipHost>
                        {/* <IconButton iconProps={{ iconName: 'GripperDotsVertical' }} styles={dragHandleStyles} /> */}
                    </Stack>
                )}

                {header && (
                    <Stack className={headerStyles}>
                        <Text variant="medium">{header}</Text>
                    </Stack>
                )}
                {footer && (
                    <Stack className={footerStyles}>
                        <Text variant="medium">{footer}</Text>
                    </Stack>
                )}
            </Stack>

            <EnlargeWidgets showImageModal={showImageModal} setShowImageModal={setShowImageModal} enlargeContent={content} type={type} />
        </>
    );
};

export default WidgetCard;
